// src/components/leave/LeaveTimeIntervalPicker.jsx
//
// Leave Time Interval Picker
// ==========================
//
// PURPOSE
// -------
// This component provides an ergonomic, calendar-style interval picker for
// short hour-based leave events, especially the hb_visits time-debt workflow:
//
// - PRIVATE_EXIT_TO_MAKE_UP  -> creates work-time debt
// - MAKE_UP_WORK             -> creates make-up credit
//
// WHY A DEDICATED COMPONENT?
// --------------------------
// The generic pair of `datetime-local` fields is technically correct, but it
// is unnecessarily expensive for the common operational case. Employees and HR
// usually think about these events as:
//
//   "on this day, from 10:00, for 45 minutes"
//
// and not as:
//
//   "start datetime + end datetime"
//
// This component keeps the backend contract unchanged.  It still emits the
// normalized `startDT` and `endDT` values expected by LeaveRequestCreateModal,
// but the UI exposes a much faster and more calendar-like interaction model:
//
// - one date
// - one start time
// - quick duration chips
// - a clickable and draggable day timeline
// - calculated end time
// - an advanced exact start/end fallback for rare edge cases
//
// FUTURE ABSENCE CALENDAR ALIGNMENT
// ---------------------------------
// The visual timeline deliberately resembles a small day-calendar strip.  It is
// not the final absence calendar module, but it prepares the mental model and
// CSS language for that future feature: the user sees the event as a time block
// on a day, not as two disconnected technical fields.
//
// IMPLEMENTATION RULES
// --------------------
// - No external date-picker dependency is introduced here.
// - All calculations use local browser time because the parent modal already
//   stores local datetime-local strings and converts them to ISO only when
//   submitting to the backend.
// - The component is fully controlled from the parent point of view: it receives
//   `startDT` / `endDT` and emits new values through `onChange`.

import React, { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

const DEFAULT_DAY_START_MINUTES = 6 * 60;
const DEFAULT_DAY_END_MINUTES = 22 * 60;
const SLOT_STEP_MINUTES = 15;

const DEFAULT_DURATION_OPTIONS = [15, 30, 45, 60, 90, 120];
const DEFAULT_START_PRESETS = [
  "08:00",
  "09:00",
  "10:00",
  "12:00",
  "14:00",
  "16:00",
  "17:00",
];

function pad2(value) {
  return String(value).padStart(2, "0");
}

function safeNumber(value, fallback = 0) {
  const n = Number(value);
  return Number.isFinite(n) ? n : fallback;
}

function toLocalISODate(date) {
  return `${date.getFullYear()}-${pad2(date.getMonth() + 1)}-${pad2(date.getDate())}`;
}

function fmtDateTimeLocal(date) {
  return `${toLocalISODate(date)}T${pad2(date.getHours())}:${pad2(date.getMinutes())}`;
}

function parseDateTimeLocal(value) {
  if (!value) return null;

  const d = new Date(value);
  if (!Number.isFinite(d.getTime())) return null;
  return d;
}

function minutesFromTimeValue(value) {
  const [hhRaw, mmRaw] = String(value || "").split(":");
  const hh = Number(hhRaw);
  const mm = Number(mmRaw);

  if (!Number.isFinite(hh) || !Number.isFinite(mm)) return null;
  if (hh < 0 || hh > 23 || mm < 0 || mm > 59) return null;

  return hh * 60 + mm;
}

function timeValueFromMinutes(totalMinutes) {
  const clamped = Math.max(0, Math.min(23 * 60 + 59, Math.trunc(totalMinutes)));
  const hh = Math.floor(clamped / 60);
  const mm = clamped % 60;
  return `${pad2(hh)}:${pad2(mm)}`;
}

function buildLocalDateTime(dateValue, timeValue) {
  if (!dateValue || !timeValue) return null;
  const d = new Date(`${dateValue}T${timeValue}`);
  return Number.isFinite(d.getTime()) ? d : null;
}

function addMinutes(date, minutes) {
  return new Date(date.getTime() + safeNumber(minutes) * 60 * 1000);
}

function differenceMinutes(start, end) {
  if (!start || !end) return 0;
  return Math.max(0, Math.round((end.getTime() - start.getTime()) / 60000));
}

function formatDuration(minutes, t) {
  const safe = Math.max(0, Math.trunc(safeNumber(minutes)));
  const h = Math.floor(safe / 60);
  const m = safe % 60;

  if (h > 0 && m > 0) {
    return t("leaves:modal.timeInterval.durationHoursMinutes", {
      defaultValue: "{{hours}}h {{minutes}}m",
      hours: h,
      minutes: m,
    });
  }

  if (h > 0) {
    return t("leaves:modal.timeInterval.durationHours", {
      defaultValue: "{{hours}}h",
      hours: h,
    });
  }

  return t("leaves:modal.timeInterval.durationMinutes", {
    defaultValue: "{{minutes}}m",
    minutes: m,
  });
}

function durationDraftFromMinutes(minutes) {
  const safe = Math.max(1, Math.trunc(safeNumber(minutes, 60)));
  const h = Math.floor(safe / 60);
  const m = safe % 60;

  if (h > 0 && m > 0) return `${h}:${pad2(m)}`;
  if (h > 0) return `${h}:00`;
  return String(m);
}

function parseDurationDraft(value) {
  const raw = String(value || "").trim().toLowerCase();
  if (!raw) return null;

  // Supports compact and common human formats used in HR/reception work:
  // - "90"       -> 90 minutes
  // - "1:30"     -> 1 hour 30 minutes
  // - "1h 30m"   -> 1 hour 30 minutes
  // - "1,5h"     -> 90 minutes for Polish/European decimal entry habits
  const colonMatch = raw.match(/^(\d{1,2})\s*:\s*(\d{1,2})$/);
  if (colonMatch) {
    const hours = Number(colonMatch[1]);
    const minutes = Number(colonMatch[2]);
    if (Number.isFinite(hours) && Number.isFinite(minutes) && minutes >= 0 && minutes < 60) {
      return hours * 60 + minutes;
    }
    return null;
  }

  const hourMinuteMatch = raw.match(/^(?:(\d+(?:[\.,]\d+)?)\s*h(?:ours?)?)?\s*(?:(\d+)\s*m(?:in(?:utes?)?)?)?$/);
  if (hourMinuteMatch && (hourMinuteMatch[1] || hourMinuteMatch[2])) {
    const hours = hourMinuteMatch[1] ? Number(String(hourMinuteMatch[1]).replace(",", ".")) : 0;
    const minutes = hourMinuteMatch[2] ? Number(hourMinuteMatch[2]) : 0;
    if (Number.isFinite(hours) && Number.isFinite(minutes)) {
      return Math.round(hours * 60 + minutes);
    }
    return null;
  }

  const numeric = Number(raw.replace(",", "."));
  if (Number.isFinite(numeric)) {
    return Math.round(numeric);
  }

  return null;
}

function clampStartMinutesForDuration(startMinutes, durationMinutes, dayStart, dayEnd) {
  const duration = Math.max(1, safeNumber(durationMinutes, SLOT_STEP_MINUTES));
  const minStart = dayStart;
  const maxStart = Math.max(dayStart, dayEnd - duration);
  return Math.max(minStart, Math.min(maxStart, Math.trunc(startMinutes)));
}

function sameLocalDate(a, b) {
  if (!a || !b) return false;
  return toLocalISODate(a) === toLocalISODate(b);
}

export default function LeaveTimeIntervalPicker({
  startDT,
  endDT,
  disabled = false,
  eventKind = "ADD_TIME_DEBT",
  onChange,
}) {
  const { t } = useTranslation(["common", "leaves"]);

  const isCreditEvent = String(eventKind || "").toUpperCase() === "REDUCE_TIME_DEBT";
  const [advancedMode, setAdvancedMode] = useState(false);
  const [dateValue, setDateValue] = useState(toLocalISODate(new Date()));
  const [startTime, setStartTime] = useState(isCreditEvent ? "16:00" : "10:00");
  const [durationMinutes, setDurationMinutes] = useState(60);
  const [durationDraft, setDurationDraft] = useState("1:00");
  const [durationDraftTouched, setDurationDraftTouched] = useState(false);
  const [isDraggingTimeline, setIsDraggingTimeline] = useState(false);
  const [isResizingEnd, setIsResizingEnd] = useState(false);

  // The timeline track itself stays clickable/draggable to move the whole
  // interval. The separate end-resize handle uses the same track geometry but
  // changes only the duration. Keeping the two pointer flows separated prevents
  // accidental start-time jumps when the user only wants to stretch the event.
  const timelineRef = useRef(null);
  const activePointerIdRef = useRef(null);
  const activeResizePointerIdRef = useRef(null);

  const parsedStart = useMemo(() => parseDateTimeLocal(startDT), [startDT]);
  const parsedEnd = useMemo(() => parseDateTimeLocal(endDT), [endDT]);

  const currentDuration = useMemo(
    () => differenceMinutes(parsedStart, parsedEnd),
    [parsedStart, parsedEnd]
  );

  const endDisplay = useMemo(() => {
    if (!parsedEnd) return "—";
    return `${pad2(parsedEnd.getHours())}:${pad2(parsedEnd.getMinutes())}`;
  }, [parsedEnd]);

  const startDisplay = useMemo(() => {
    if (!parsedStart) return "—";
    return `${pad2(parsedStart.getHours())}:${pad2(parsedStart.getMinutes())}`;
  }, [parsedStart]);

  const crossesMidnight = useMemo(
    () => Boolean(parsedStart && parsedEnd && !sameLocalDate(parsedStart, parsedEnd)),
    [parsedStart, parsedEnd]
  );

  /**
   * Keep the ergonomic fields synchronized with the controlled parent values.
   * This is important when:
   * - the modal initializes defaults,
   * - the user changes employee / leave type and parent resets dates,
   * - the user edits the exact datetime values in advanced mode.
   */
  useEffect(() => {
    if (!parsedStart || !parsedEnd) return;

    const duration = differenceMinutes(parsedStart, parsedEnd);
    if (duration <= 0) return;

    setDateValue(toLocalISODate(parsedStart));
    setStartTime(`${pad2(parsedStart.getHours())}:${pad2(parsedStart.getMinutes())}`);
    setDurationMinutes(duration);

    if (!durationDraftTouched) {
      setDurationDraft(durationDraftFromMinutes(duration));
    }
  }, [parsedStart, parsedEnd, durationDraftTouched]);

  function commitInterval(nextDateValue, nextStartTime, nextDurationMinutes) {
    const start = buildLocalDateTime(nextDateValue, nextStartTime);
    const duration = Math.max(1, Math.trunc(safeNumber(nextDurationMinutes, 60)));

    if (!start) return;

    const end = addMinutes(start, duration);
    onChange?.({
      startDT: fmtDateTimeLocal(start),
      endDT: fmtDateTimeLocal(end),
    });
  }

  function onDateChange(value) {
    setDateValue(value);
    commitInterval(value, startTime, durationMinutes);
  }

  function onStartTimeChange(value) {
    setStartTime(value);
    commitInterval(dateValue, value, durationMinutes);
  }

  function commitDurationMinutes(minutes) {
    const nextMinutes = Math.max(1, Math.trunc(safeNumber(minutes, 60)));
    setDurationMinutes(nextMinutes);
    setDurationDraft(durationDraftFromMinutes(nextMinutes));
    setDurationDraftTouched(false);
    commitInterval(dateValue, startTime, nextMinutes);
  }

  function onDurationChipClick(value) {
    commitDurationMinutes(value);
  }

  function onDurationDraftChange(value) {
    setDurationDraftTouched(true);
    setDurationDraft(value);
  }

  function onDurationDraftCommit() {
    const parsed = parseDurationDraft(durationDraft);

    if (!parsed || parsed <= 0) {
      setDurationDraft(durationDraftFromMinutes(durationMinutes));
      setDurationDraftTouched(false);
      return;
    }

    commitDurationMinutes(parsed);
  }

  function onDurationDraftKeyDown(event) {
    if (event.key === "Enter") {
      event.preventDefault();
      onDurationDraftCommit();
    }

    if (event.key === "Escape") {
      event.preventDefault();
      setDurationDraft(durationDraftFromMinutes(durationMinutes));
      setDurationDraftTouched(false);
    }
  }

  function onExactStartChange(value) {
    const nextStart = parseDateTimeLocal(value);
    if (!nextStart || !parsedEnd || parsedEnd <= nextStart) {
      onChange?.({ startDT: value, endDT });
      return;
    }

    onChange?.({ startDT: value, endDT });
  }

  function onExactEndChange(value) {
    onChange?.({ startDT, endDT: value });
  }

  function moveTimelineSelectionFromClientX(clientX, timelineElement) {
    if (disabled || !timelineElement) return;

    const rect = timelineElement.getBoundingClientRect();
    if (!rect.width) return;

    const ratio = Math.max(0, Math.min(1, (clientX - rect.left) / rect.width));
    const daySpan = DEFAULT_DAY_END_MINUTES - DEFAULT_DAY_START_MINUTES;
    const rawMinutes = DEFAULT_DAY_START_MINUTES + ratio * daySpan;
    const rounded = Math.round(rawMinutes / SLOT_STEP_MINUTES) * SLOT_STEP_MINUTES;
    const clamped = clampStartMinutesForDuration(
      rounded,
      durationMinutes,
      DEFAULT_DAY_START_MINUTES,
      DEFAULT_DAY_END_MINUTES
    );

    onStartTimeChange(timeValueFromMinutes(clamped));
  }

  function durationFromTimelineEndClientX(clientX) {
    const timelineElement = timelineRef.current;
    const startMinutes = minutesFromTimeValue(startTime);

    if (!timelineElement || startMinutes == null) return null;

    const rect = timelineElement.getBoundingClientRect();
    if (!rect.width) return null;

    const ratio = Math.max(0, Math.min(1, (clientX - rect.left) / rect.width));
    const daySpan = DEFAULT_DAY_END_MINUTES - DEFAULT_DAY_START_MINUTES;
    const rawEndMinutes = DEFAULT_DAY_START_MINUTES + ratio * daySpan;
    const roundedEndMinutes = Math.round(rawEndMinutes / SLOT_STEP_MINUTES) * SLOT_STEP_MINUTES;

    // The resize handle represents the right edge of the selected block. The
    // minimum supported duration is one configured slot, currently 15 minutes,
    // which matches the business expectation for private exits and make-up work.
    const minEndMinutes = startMinutes + SLOT_STEP_MINUTES;
    const maxEndMinutes = DEFAULT_DAY_END_MINUTES;
    const clampedEndMinutes = Math.max(minEndMinutes, Math.min(maxEndMinutes, roundedEndMinutes));

    return Math.max(SLOT_STEP_MINUTES, clampedEndMinutes - startMinutes);
  }

  function resizeDurationFromTimelineEnd(clientX) {
    const nextDuration = durationFromTimelineEndClientX(clientX);
    if (!nextDuration) return;

    commitDurationMinutes(nextDuration);
  }

  function onSelectionEndResizePointerDown(event) {
    if (disabled) return;
    if (event.pointerType === "mouse" && event.button !== 0) return;

    // Stop the event before it reaches the timeline track. Otherwise the same
    // pointer-down would first move the whole interval and only then resize it,
    // which feels imprecise for the user.
    event.preventDefault();
    event.stopPropagation();

    activeResizePointerIdRef.current = event.pointerId;
    setIsResizingEnd(true);
    event.currentTarget.setPointerCapture?.(event.pointerId);
    resizeDurationFromTimelineEnd(event.clientX);
  }

  function onSelectionEndResizePointerMove(event) {
    if (disabled || !isResizingEnd) return;
    if (activeResizePointerIdRef.current !== event.pointerId) return;

    event.preventDefault();
    event.stopPropagation();
    resizeDurationFromTimelineEnd(event.clientX);
  }

  function finishSelectionEndResize(event) {
    if (activeResizePointerIdRef.current === event.pointerId) {
      event.currentTarget.releasePointerCapture?.(event.pointerId);
      activeResizePointerIdRef.current = null;
    }

    setIsResizingEnd(false);
  }

  function onTimelinePointerDown(event) {
    if (disabled) return;

    // Only the primary pointer should control the timeline. This avoids noisy
    // updates from secondary mouse buttons or multi-touch gestures. Pointer
    // capture keeps the drag active even when the cursor leaves the track.
    if (event.pointerType === "mouse" && event.button !== 0) return;

    event.preventDefault();
    activePointerIdRef.current = event.pointerId;
    setIsDraggingTimeline(true);
    event.currentTarget.setPointerCapture?.(event.pointerId);
    moveTimelineSelectionFromClientX(event.clientX, event.currentTarget);
  }

  function onTimelinePointerMove(event) {
    if (disabled || !isDraggingTimeline) return;
    if (activePointerIdRef.current !== event.pointerId) return;

    event.preventDefault();
    moveTimelineSelectionFromClientX(event.clientX, event.currentTarget);
  }

  function finishTimelineDrag(event) {
    if (activePointerIdRef.current === event.pointerId) {
      event.currentTarget.releasePointerCapture?.(event.pointerId);
      activePointerIdRef.current = null;
    }

    setIsDraggingTimeline(false);
  }

  function onTimelineKeyDown(event) {
    if (disabled) return;

    const currentStart = minutesFromTimeValue(startTime);
    if (currentStart == null) return;

    let nextStart = currentStart;
    const smallStep = SLOT_STEP_MINUTES;
    const largeStep = 60;
    const step = event.shiftKey ? largeStep : smallStep;

    if (event.key === "ArrowLeft") nextStart -= step;
    else if (event.key === "ArrowRight") nextStart += step;
    else if (event.key === "Home") nextStart = DEFAULT_DAY_START_MINUTES;
    else if (event.key === "End") nextStart = DEFAULT_DAY_END_MINUTES;
    else return;

    event.preventDefault();

    const clamped = clampStartMinutesForDuration(
      nextStart,
      durationMinutes,
      DEFAULT_DAY_START_MINUTES,
      DEFAULT_DAY_END_MINUTES
    );
    onStartTimeChange(timeValueFromMinutes(clamped));
  }

  const timelineGeometry = useMemo(() => {
    const startMinutes = minutesFromTimeValue(startTime);
    const duration = Math.max(1, safeNumber(durationMinutes, SLOT_STEP_MINUTES));
    const daySpan = DEFAULT_DAY_END_MINUTES - DEFAULT_DAY_START_MINUTES;

    if (startMinutes == null || daySpan <= 0) {
      return { left: 0, width: 0 };
    }

    const left = ((startMinutes - DEFAULT_DAY_START_MINUTES) / daySpan) * 100;
    const width = (duration / daySpan) * 100;

    return {
      left: Math.max(0, Math.min(100, left)),
      width: Math.max(2, Math.min(100, width)),
    };
  }, [startTime, durationMinutes]);

  const timelineHours = useMemo(() => {
    const hours = [];
    for (let h = DEFAULT_DAY_START_MINUTES / 60; h <= DEFAULT_DAY_END_MINUTES / 60; h += 2) {
      hours.push(`${pad2(h)}:00`);
    }
    return hours;
  }, []);

  return (
    <div className="lrcm-timeIntervalPicker">
      <div className="lrcm-timeIntervalPicker__header">
        <div>
          <div className="lrcm-timeIntervalPicker__eyebrow">
            {t("leaves:modal.timeInterval.calendarStylePicker", {
              defaultValue: "Calendar-style time picker",
            })}
          </div>
          <div className="lrcm-timeIntervalPicker__summary">
            {t("leaves:modal.timeInterval.summary", {
              defaultValue: "{{start}} → {{end}} · {{duration}}",
              start: startDisplay,
              end: endDisplay,
              duration: formatDuration(currentDuration || durationMinutes, t),
            })}
          </div>
        </div>

        <button
          type="button"
          className="lrcm-timeIntervalPicker__advancedToggle"
          onClick={() => setAdvancedMode((prev) => !prev)}
          disabled={disabled}
        >
          {advancedMode
            ? t("leaves:modal.timeInterval.hideExactFields", { defaultValue: "Hide exact fields" })
            : t("leaves:modal.timeInterval.editExactFields", { defaultValue: "Edit exact start/end" })}
        </button>
      </div>

      <div className="lrcm-timeIntervalPicker__quickGrid">
        <div className="lrcm-field">
          <label>{t("leaves:modal.timeInterval.date", { defaultValue: "Date" })}</label>
          <input
            type="date"
            value={dateValue}
            onChange={(event) => onDateChange(event.target.value)}
            disabled={disabled}
          />
        </div>

        <div className="lrcm-field">
          <label>{t("leaves:modal.timeInterval.startTime", { defaultValue: "Start time" })}</label>
          <input
            type="time"
            step={SLOT_STEP_MINUTES * 60}
            value={startTime}
            onChange={(event) => onStartTimeChange(event.target.value)}
            disabled={disabled}
          />
        </div>

        <div className="lrcm-field">
          <label>{t("leaves:modal.timeInterval.duration", { defaultValue: "Duration" })}</label>
          <input
            type="text"
            inputMode="text"
            value={durationDraft}
            onChange={(event) => onDurationDraftChange(event.target.value)}
            onBlur={onDurationDraftCommit}
            onKeyDown={onDurationDraftKeyDown}
            disabled={disabled}
            placeholder={t("leaves:modal.timeInterval.durationPlaceholder", {
              defaultValue: "e.g. 90, 1:30, 1h 30m",
            })}
          />
          <small className="lrcm-timeIntervalPicker__durationHint">
            {t("leaves:modal.timeInterval.durationHint", {
              defaultValue: "Enter minutes or a duration like 1:30 / 1h 30m. Press Enter to apply.",
            })}
          </small>
        </div>
      </div>

      <div className="lrcm-timeIntervalPicker__chipsBlock">
        <div className="lrcm-timeIntervalPicker__chipsLabel">
          {t("leaves:modal.timeInterval.quickStart", { defaultValue: "Quick start" })}
        </div>
        <div className="lrcm-timeIntervalPicker__chips">
          {DEFAULT_START_PRESETS.map((preset) => (
            <button
              key={preset}
              type="button"
              className={`lrcm-timeIntervalPicker__chip${preset === startTime ? " is-active" : ""}`}
              onClick={() => onStartTimeChange(preset)}
              disabled={disabled}
            >
              {preset}
            </button>
          ))}
        </div>
      </div>

      <div className="lrcm-timeIntervalPicker__chipsBlock">
        <div className="lrcm-timeIntervalPicker__chipsLabel">
          {t("leaves:modal.timeInterval.quickDuration", { defaultValue: "Quick duration" })}
        </div>
        <div className="lrcm-timeIntervalPicker__chips">
          {DEFAULT_DURATION_OPTIONS.map((minutes) => (
            <button
              key={minutes}
              type="button"
              className={`lrcm-timeIntervalPicker__chip${Number(durationMinutes) === minutes ? " is-active" : ""}`}
              onClick={() => onDurationChipClick(minutes)}
              disabled={disabled}
            >
              {formatDuration(minutes, t)}
            </button>
          ))}
        </div>
      </div>

      <div className="lrcm-timeIntervalPicker__timelineWrap">
        <div className="lrcm-timeIntervalPicker__timelineHelp">
          {t("leaves:modal.timeInterval.timelineHelp", {
            defaultValue: "Click or drag the selected interval on the day timeline. Exact duration can still be typed above.",
          })}
          <span className="lrcm-timeIntervalPicker__timelineResizeHint">
            {t("leaves:modal.timeInterval.timelineResizeHelp", {
              defaultValue: "Drag the right edge to change duration in 15-minute steps.",
            })}
          </span>
        </div>

        <div
          ref={timelineRef}
          role="slider"
          tabIndex={disabled ? -1 : 0}
          className={`lrcm-timeIntervalPicker__timeline${isDraggingTimeline ? " is-dragging" : ""}${isResizingEnd ? " is-resizing" : ""}${disabled ? " is-disabled" : ""}`}
          onPointerDown={onTimelinePointerDown}
          onPointerMove={onTimelinePointerMove}
          onPointerUp={finishTimelineDrag}
          onPointerCancel={finishTimelineDrag}
          onKeyDown={onTimelineKeyDown}
          aria-disabled={disabled ? "true" : "false"}
          aria-valuemin={DEFAULT_DAY_START_MINUTES}
          aria-valuemax={DEFAULT_DAY_END_MINUTES}
          aria-valuenow={minutesFromTimeValue(startTime) || DEFAULT_DAY_START_MINUTES}
          aria-label={t("leaves:modal.timeInterval.timelineAria", {
            defaultValue: "Move selected time interval on the day timeline",
          })}
          aria-valuetext={t("leaves:modal.timeInterval.timelineValueText", {
            defaultValue: "{{start}} to {{end}}, {{duration}}",
            start: startDisplay,
            end: endDisplay,
            duration: formatDuration(currentDuration || durationMinutes, t),
          })}
        >
          <span
            className="lrcm-timeIntervalPicker__selection"
            style={{
              left: `${timelineGeometry.left}%`,
              width: `${timelineGeometry.width}%`,
            }}
          >
            <span className="lrcm-timeIntervalPicker__selectionHandle" aria-hidden="true" />
          <span
            className="lrcm-timeIntervalPicker__selectionResizeHandle"
            role="separator"
            aria-orientation="vertical"
            tabIndex={disabled ? -1 : 0}
            aria-label={t("leaves:modal.timeInterval.resizeEndHandleAria", {
              defaultValue: "Resize selected interval duration",
            })}
            title={t("leaves:modal.timeInterval.resizeEndHandleTitle", {
              defaultValue: "Drag to change duration in 15-minute steps",
            })}
            onPointerDown={onSelectionEndResizePointerDown}
            onPointerMove={onSelectionEndResizePointerMove}
            onPointerUp={finishSelectionEndResize}
            onPointerCancel={finishSelectionEndResize}
          />
          </span>
        </div>

        <div className="lrcm-timeIntervalPicker__ticks" aria-hidden="true">
          {timelineHours.map((hour) => (
            <span key={hour}>{hour}</span>
          ))}
        </div>
      </div>

      {crossesMidnight && (
        <div className="lrcm-timeIntervalPicker__warning">
          {t("leaves:modal.timeInterval.crossesMidnightWarning", {
            defaultValue: "This interval crosses midnight. Please verify exact start and end values before submitting.",
          })}
        </div>
      )}

      {Number(durationMinutes) > 240 && (
        <div className="lrcm-timeIntervalPicker__warning">
          {t("leaves:modal.timeInterval.longDurationWarning", {
            defaultValue: "The selected duration is longer than 4 hours. This is allowed, but please check whether this should really be a time-debt event.",
          })}
        </div>
      )}

      {advancedMode && (
        <div className="lrcm-timeIntervalPicker__advancedGrid">
          <div className="lrcm-field">
            <label>{t("leaves:modal.startDateTime", { defaultValue: "Start date/time" })}</label>
            <input
              type="datetime-local"
              value={startDT || ""}
              onChange={(event) => onExactStartChange(event.target.value)}
              disabled={disabled}
            />
          </div>

          <div className="lrcm-field">
            <label>{t("leaves:modal.endDateTime", { defaultValue: "End date/time" })}</label>
            <input
              type="datetime-local"
              value={endDT || ""}
              onChange={(event) => onExactEndChange(event.target.value)}
              disabled={disabled}
            />
          </div>
        </div>
      )}
    </div>
  );
}
